import React, { useCallback, useEffect, useState } from 'react';
import { FiCheck, FiMinus } from 'react-icons/fi';
import { subscriptionApi } from '../services/api';
import { getApiErrorMessage } from '../utils/examStart';
import { useAuth } from '../hooks/useAuth';
import { formatDay, formatKrw, type SubscriptionSummary } from '../hooks/useUpgrade';
import { Button, Card, LoadingState, PageHeader } from '../components/ui';
import './SubscriptionPage.css';

const FEATURES: Array<{ name: string; free: boolean; power: boolean }> = [
  { name: '샘플 시험 응시, 결과·해설', free: true, power: true },
  { name: '틀린 문제 다시 풀기 (연습 모드)', free: true, power: true },
  { name: '오답 모의고사 (틀린 문제 + 약한 영역으로 실전 구성)', free: false, power: true },
  { name: '본 시험 전체 응시 (쿠폰 없이)', free: false, power: true },
  { name: 'AI 학습 진단 리포트 (영역별 진단·오답 패턴·보강 계획)', free: false, power: true },
  { name: '광고 없음', free: false, power: true },
];

const daysLeft = (iso?: string | null) => (iso ? Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000)) : 0);

/** 요금제 + 내 구독. 구독 안내 팝업의 "플랜 선택"이 여기로 옵니다. */
const SubscriptionPage: React.FC = () => {
  const { refreshUser } = useAuth();
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await subscriptionApi.me() as { data?: SubscriptionSummary };
      setSummary(response.data || null);
      setError(null);
    } catch (loadError) {
      setError(getApiErrorMessage(loadError) || '구독 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startTrial = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await subscriptionApi.startTrial() as { data?: SubscriptionSummary };
      setSummary(response.data || null);
      await refreshUser();
    } catch (startError) {
      setError(getApiErrorMessage(startError) || '무료 체험을 시작하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingState label="구독 정보를 불러오는 중..." />;

  const plan = summary?.plan || { name: 'AI Power User', price: 10000, trialDays: 30 };
  const power = summary?.entitlement;
  const status = summary?.subscription?.status;

  const statusBlock = (() => {
    if (power?.source === 'admin') return { title: '관리자 계정', body: '관리자는 구독 없이 모든 AI Power User 기능을 씁니다.' };
    if (power?.source === 'manual') return { title: '이용 기간 부여됨', body: `관리자가 부여한 이용 기간이 ${formatDay(power.until)}까지 남아 있습니다 (D-${daysLeft(power.until)}).` };
    if (power?.source === 'trial') return { title: `무료 체험 중 · D-${daysLeft(power.until)}`, body: `${formatDay(power.until)}에 체험이 끝납니다. 체험이 끝나도 자동으로 결제되지 않으니, 계속 쓰려면 아래에서 구독해 주세요.` };
    if (power?.source === 'subscription') return { title: '구독 중', body: summary?.subscription?.next_billing_at ? `다음 결제일은 ${formatDay(summary.subscription.next_billing_at)}입니다.` : '구독이 유지되고 있습니다.' };
    if (status === 'expired' || summary?.trialAvailable === false) return { title: '일반 이용 중', body: '무료 체험이 끝났습니다. 구독하면 AI 기능을 다시 쓸 수 있습니다.' };
    return { title: '일반 이용 중', body: `카드 등록 없이 ${plan.trialDays}일 동안 AI Power User 기능을 무료로 써 볼 수 있습니다.` };
  })();

  const canTrial = Boolean(summary?.trialAvailable && !power?.aiPower);
  const needsSubscribe = !power?.aiPower || power.source === 'trial' || power.source === 'manual';

  return (
    <div className="sub-page">
      <PageHeader eyebrow="요금제" title="AI Power User" description="AI가 시험 결과를 진단하고 보강 계획을 세워 줍니다." />

      <Card className="sub-status" padding="lg" shadow="sm">
        <div>
          <p className="sub-status__label">내 이용 상태</p>
          <h2>{statusBlock.title}</h2>
          <p className="sub-status__body">{statusBlock.body}</p>
        </div>
        {error && <p className="sub-error" role="alert">{error}</p>}
      </Card>

      <div className="sub-plans">
        <Card className="sub-plan" padding="lg" shadow="sm">
          <p className="sub-plan__name">일반</p>
          <p className="sub-plan__price tabular">0원</p>
          <p className="sub-plan__note">기본 시험 기능</p>
        </Card>

        <Card className="sub-plan sub-plan--power" padding="lg" shadow="md">
          <p className="sub-plan__name">{plan.name}</p>
          <p className="sub-plan__price tabular">{formatKrw(plan.price)}<small> / 월</small></p>
          <p className="sub-plan__note">{canTrial ? `첫 ${plan.trialDays}일 무료 · 카드 등록 없이 시작` : '언제든 해지'}</p>
          <div className="sub-plan__action">
            {canTrial ? (
              <Button variant="primary" fullWidth onClick={startTrial} loading={busy}>{plan.trialDays}일 무료 체험 시작</Button>
            ) : needsSubscribe && power?.source !== 'admin' ? (
              <>
                <Button variant="primary" fullWidth disabled={!summary?.billingReady}>
                  월 {formatKrw(plan.price)} 구독하기
                </Button>
                {!summary?.billingReady && <p className="sub-plan__hint">결제 연동을 준비하고 있습니다. 준비되면 메시지함으로 알려 드릴게요.</p>}
              </>
            ) : (
              <p className="sub-plan__hint">이미 이용 중입니다.</p>
            )}
          </div>
        </Card>
      </div>

      <Card padding="lg" shadow="sm" className="sub-compare">
        <h2>기능 비교</h2>
        <table>
          <thead>
            <tr>
              <th scope="col">기능</th>
              <th scope="col">일반</th>
              <th scope="col">AI Power User</th>
            </tr>
          </thead>
          <tbody>
            {FEATURES.map((feature) => (
              <tr key={feature.name}>
                <th scope="row">{feature.name}</th>
                <td>{feature.free ? <FiCheck aria-label="포함" /> : <FiMinus aria-label="미포함" className="is-off" />}</td>
                <td>{feature.power ? <FiCheck aria-label="포함" /> : <FiMinus aria-label="미포함" className="is-off" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <ul className="sub-notes">
        <li>무료 체험은 계정당 한 번이며, 체험이 끝나도 자동으로 결제되지 않습니다.</li>
        <li>구독하면 매월 같은 날 자동 결제되고, 해지하면 이미 결제한 기간 끝까지 쓸 수 있습니다.</li>
        <li>카드 정보는 토스페이먼츠 결제창에서만 입력하며, 이 서비스는 카드번호를 저장하지 않습니다.</li>
      </ul>
    </div>
  );
};

export default SubscriptionPage;
